import { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Download, ExternalLink, Loader2, UserPlus, MapPinned, Wand2, ChevronDown, ChevronRight, Link2 } from "lucide-react";
import FAStagingPanel from "@/components/app/FAStagingPanel";
import AdminFARulesEditor from "@/components/app/AdminFARulesEditor";

const HH = Array.from({length: 24}, (_, i) => i);
const MM = [0, 15, 30, 45];

export default function AdminFAImport() {
  const [url, setUrl] = useState("");
  const [defaultKickoff, setDefaultKickoff] = useState("10:00");
  const [clubPrefix, setClubPrefix] = useState("Elsenham");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [squadsCreating, setSquadsCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [stagingReloadKey, setStagingReloadKey] = useState(0);
  const [showRules, setShowRules] = useState(false);
  const [unmatchedResolving, setUnmatchedResolving] = useState({}); // fa_name -> team_id

  const preview = async () => {
    if (!url.trim()) return toast.error("Paste an FA Full-Time URL first");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/fixtures/fa-preview", {
        url: url.trim(),
        default_kickoff: defaultKickoff,
        club_prefix: clubPrefix,
      });
      setResult(data);
      setRows(data.fixtures.map(f => ({
        ...f,
        selected: !f.already_imported && !!f.matched_team_id,
      })));
      toast.success(`Found ${data.count_home} home fixture(s) out of ${data.count_total} total`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Preview failed");
    } finally { setLoading(false); }
  };

  const missingSquads = useMemo(
    () => (result?.club_squads || []).filter(s => !s.already_exists),
    [result]
  );

  const unmatchedNames = useMemo(() => (result?.unmatched_names || []).map(n => n.fa_name), [result]);

  const saveOneMapping = async (faName) => {
    const teamId = unmatchedResolving[faName];
    if (!teamId) return toast.error("Pick a squad first");
    try {
      await api.post("/fa/team-map/add", { fa_name: faName, team_id: teamId });
      toast.success(`Mapped '${faName}'`);
      await preview();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
  };

  const createMissingSquads = async () => {
    if (missingSquads.length === 0) return;
    setSquadsCreating(true);
    try {
      const { data } = await api.post("/admin/fixtures/fa-create-squads", {
        names: missingSquads.map(s => s.name),
        club_prefix: clubPrefix,
      });
      toast.success(`Created ${data.created} squad${data.created === 1 ? "" : "s"}${data.skipped.length ? ` — ${data.skipped.length} skipped` : ""}`);
      // Re-run preview so team matches populate on the fixture rows
      await preview();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Squad create failed");
    } finally { setSquadsCreating(false); }
  };

  const update = (idx, patch) => setRows(prev => prev.map((r, i) => i === idx ? {...r, ...patch} : r));

  const selectedCount = useMemo(() => rows.filter(r => r.selected && !r.already_imported).length, [rows]);
  const allSelectable = useMemo(() => rows.filter(r => !r.already_imported), [rows]);
  const toggleAll = (v) => setRows(prev => prev.map(r => r.already_imported ? r : {...r, selected: v}));

  // ---------- Bulk pitch ----------
  const [bulkPitchId, setBulkPitchId] = useState("");
  const applyBulkPitch = () => {
    if (!bulkPitchId) return toast.error("Pick a pitch first");
    const pitch = (result?.pitches || []).find(p => p.id === bulkPitchId);
    if (!pitch) return;
    setRows(prev => prev.map(r =>
      (r.selected && !r.already_imported)
        ? {...r, suggested_pitch_id: bulkPitchId, suggested_pitch_name: pitch.name}
        : r
    ));
    const n = rows.filter(r => r.selected && !r.already_imported).length;
    toast.success(`Set ${n} row${n === 1 ? "" : "s"} to ${pitch.name}`);
  };

  // ---------- Auto-schedule algorithm ----------
  // Groups selected rows by DATE, sorts youngest age group first, and stacks each
  // pitch from defaultKickoff. When a pitch overflows past its close_hour or the
  // configured "latest kick-off", the algorithm spills the fixture onto the next
  // available pitch — preferring pitches at the same venue first, then any pitch.
  const [turnaroundMin, setTurnaroundMin] = useState(15);
  const [latestKickoff, setLatestKickoff] = useState("15:00");
  const normalizedLatest = useMemo(() => {
    const m = /^\s*(\d{1,2}):(\d{2})\s*$/.exec(latestKickoff || "");
    if (!m) return "15:00";
    const h = parseInt(m[1]), min = parseInt(m[2]);
    if (isNaN(h) || isNaN(min) || h < 0 || h > 23 || min < 0 || min > 59) return "15:00";
    return `${String(h).padStart(2,"0")}:${String(min).padStart(2,"0")}`;
  }, [latestKickoff]);
  const parseAge = (ag) => {
    const m = /U(\d+)/i.exec(ag || "");
    return m ? parseInt(m[1]) : 99;
  };
  const snapUp15 = (total) => (total % 15 === 0 ? total : (Math.floor(total / 15) + 1) * 15);
  const parseHM = (s, fb) => {
    const m = /^\s*(\d{1,2}):(\d{2})\s*$/.exec(s || "");
    if (!m) return fb;
    const h = parseInt(m[1]), min = parseInt(m[2]);
    if (isNaN(h) || isNaN(min) || h < 0 || h > 23 || min < 0 || min > 59) return fb;
    return [h, min];
  };

  const autoSchedule = () => {
    const [bh, bm] = parseHM(defaultKickoff, [10, 0]);
    const baseTotal = Math.floor((bh * 60 + bm) / 15) * 15;
    const [lh, lm] = parseHM(latestKickoff, [15, 0]);
    const latestTotal = lh * 60 + lm;

    const pitchById = Object.fromEntries((result?.pitches || []).map(p => [p.id, p]));

    // Existing DB bookings we must not overlap. Key: "pitch_id||date" -> sorted [{s,e}]
    const busy = {};
    (result?.existing_bookings || []).forEach(b => {
      const key = `${b.pitch_id}||${b.date}`;
      if (!busy[key]) busy[key] = [];
      busy[key].push({s: b.start_min, e: b.end_min});
    });
    Object.values(busy).forEach(list => list.sort((a, b) => a.s - b.s));

    // Group selected + unimported rows by DATE
    const byDate = new Map();
    rows.forEach((r, idx) => {
      if (!r.selected || r.already_imported || !r.suggested_pitch_id) return;
      if (!byDate.has(r.date)) byDate.set(r.date, []);
      byDate.get(r.date).push({r, idx});
    });

    // Find earliest 15-min-aligned start ≥ cursor s.t. [start, start+dur] doesn't overlap
    // any busy interval (either DB or in-batch). Applies `turnaroundMin` padding on both
    // sides of every busy interval so a fixture never sits back-to-back with a real booking.
    const findFreeStart = (cursor, dur, busyList, openTotal, closeTotal) => {
      let start = Math.max(cursor, openTotal);
      start = start % 15 === 0 ? start : (Math.floor(start / 15) + 1) * 15;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const end = snapUp15(start + dur);
        if (end > closeTotal) return null;
        // Treat every busy interval as [s - turnaround, e + turnaround) for overlap testing
        const clash = busyList.find(x => (x.s - turnaroundMin) < end && (x.e + turnaroundMin) > start);
        if (!clash) return {start, end};
        // Jump past the conflict, snap-up to 15-min, honour turnaround
        start = snapUp15(clash.e + turnaroundMin);
      }
    };

    const updates = {};
    let spillCount = 0;
    let overflowCount = 0;
    let dbCollisionAvoids = 0;

    for (const [, list] of byDate.entries()) {
      // Youngest first
      list.sort((a, b) => parseAge(b.r.matched_age_group) - parseAge(a.r.matched_age_group));
      // Per-pitch busy list starts from the DB bookings; we add our own placements as we go
      const localBusy = {}; // pitch_id -> [{s,e}]
      const cursors = {};   // pitch_id -> next-earliest cursor
      const key = (pid, d) => `${pid}||${d}`;

      for (const {r, idx} of list) {
        const dur = r.duration_min || 60;
        const preferredId = r.suggested_pitch_id;
        const preferred = pitchById[preferredId];
        const preferredVenue = preferred?.venue_id;

        // Candidate order: preferred → same venue → any other pitch
        const others = (result?.pitches || []).filter(p => p.id !== preferredId);
        const candidateIds = [
          preferredId,
          ...others.filter(p => p.venue_id === preferredVenue).map(p => p.id),
          ...others.filter(p => p.venue_id !== preferredVenue).map(p => p.id),
        ];

        let chosen = null;
        let start = 0, end = 0;
        for (const cid of candidateIds) {
          const p = pitchById[cid];
          if (!p) continue;
          const openTotal = (p.open_hour ?? 8) * 60;
          const closeTotal = (p.close_hour ?? 22) * 60;
          const cursor = cursors[cid] ?? Math.max(baseTotal, openTotal);
          const dbBusy = busy[key(cid, r.date)] || [];
          const scheduledBusy = localBusy[cid] || [];
          const merged = [...dbBusy, ...scheduledBusy].sort((a, b) => a.s - b.s);
          const found = findFreeStart(cursor, dur, merged, openTotal, closeTotal);
          if (!found) continue;
          // Track DB-avoidance whether or not this candidate ends up being chosen
          const skippedDb = dbBusy.some(x => x.s >= cursor && x.s < found.start);
          if (found.start > latestTotal) continue;
          if (skippedDb) dbCollisionAvoids++;
          chosen = cid;
          start = found.start;
          end = found.end;
          break;
        }

        let markLate = false;
        if (!chosen) {
          // Nothing fits under the latest kickoff — try preferred pitch again but ignore latestTotal
          chosen = preferredId;
          const p = pitchById[chosen];
          const openTotal = (p?.open_hour ?? 8) * 60;
          const closeTotal = (p?.close_hour ?? 22) * 60;
          const cursor = cursors[chosen] ?? Math.max(baseTotal, openTotal);
          const dbBusy = busy[key(chosen, r.date)] || [];
          const scheduledBusy = localBusy[chosen] || [];
          const merged = [...dbBusy, ...scheduledBusy].sort((a, b) => a.s - b.s);
          const found = findFreeStart(cursor, dur, merged, openTotal, closeTotal) || {start: cursor, end: snapUp15(cursor + dur)};
          start = found.start; end = found.end;
          markLate = true;
          overflowCount++;
        }

        cursors[chosen] = end + turnaroundMin;
        if (!localBusy[chosen]) localBusy[chosen] = [];
        localBusy[chosen].push({s: start, e: end});

        const moved = chosen !== preferredId;
        if (moved) spillCount++;

        updates[idx] = {
          suggested_pitch_id: chosen,
          suggested_pitch_name: pitchById[chosen]?.name,
          suggested_start_hour: Math.floor(start / 60),
          suggested_start_min: start % 60,
          suggested_end_hour: Math.floor(end / 60),
          suggested_end_min: end % 60,
          spillover_moved_from: moved ? (preferred?.name || null) : null,
          spillover_late: markLate,
        };
      }
    }

    const touched = Object.keys(updates).length;
    if (touched === 0) return toast.error("Select rows first (with a pitch assigned)");
    setRows(prev => prev.map((r, i) => updates[i] ? {...r, ...updates[i]} : {...r, spillover_moved_from: null, spillover_late: false}));

    let msg = `Auto-scheduled ${touched} fixture${touched === 1 ? "" : "s"} across ${byDate.size} day${byDate.size === 1 ? "" : "s"}`;
    if (spillCount > 0) msg += ` · ${spillCount} spilled to spare pitch${spillCount === 1 ? "" : "es"}`;
    if (dbCollisionAvoids > 0) msg += ` · avoided ${dbCollisionAvoids} existing booking${dbCollisionAvoids === 1 ? "" : "s"}`;
    toast.success(msg);
    if (overflowCount > 0) toast.warning(`${overflowCount} fixture${overflowCount === 1 ? "" : "s"} couldn't find a free pitch before ${normalizedLatest} — please review.`);
  };

  const importSelected = async () => {
    const chosen = rows.filter(r => r.selected && !r.already_imported);
    if (chosen.length === 0) return toast.error("Nothing selected");
    if (chosen.some(r => !r.suggested_pitch_id)) return toast.error("Every selected row needs a pitch");
    setImporting(true);
    try {
      const { data } = await api.post("/admin/fa-staging/add", {
        items: chosen.map(r => ({
          fa_id: r.fa_id,
          booking_date: r.date,
          start_hour: r.suggested_start_hour,
          start_min: r.suggested_start_min || 0,
          end_hour: r.suggested_end_hour,
          end_min: r.suggested_end_min || 0,
          pitch_id: r.suggested_pitch_id || null,
          team_id: r.matched_team_id || null,
          opponent: r.away_team || null,
          competition: r.competition || null,
          home_team_name: r.home_team || null,
          age_group: r.matched_age_group || null,
        })),
      });
      toast.success(`${data.added} sent to staging${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`);
      // Mark sent rows as done so they can't be re-sent
      const sentIds = new Set(chosen.map(r => r.fa_id));
      const skippedIds = new Set(data.skipped.map(s => s.fa_id));
      setRows(prev => prev.map(r => {
        if (!sentIds.has(r.fa_id)) return r;
        return skippedIds.has(r.fa_id) ? {...r, skip_reason: (data.skipped.find(s => s.fa_id === r.fa_id) || {}).reason, selected: false}
          : {...r, already_imported: true, selected: false};
      }));
      setStagingReloadKey(k => k + 1);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Send to staging failed"); }
    finally { setImporting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="card-tactical rounded-md overflow-hidden">
        <button
          type="button"
          onClick={() => setShowRules(v => !v)}
          data-testid="fa-rules-toggle"
          className="w-full flex items-center gap-3 px-5 py-4 hover:bg-muted/40 transition-colors text-left"
        >
          {showRules ? <ChevronDown className="w-4 h-4 opacity-70" /> : <ChevronRight className="w-4 h-4 opacity-70" />}
          <div className="flex-1">
            <div className="font-display font-bold text-lg tracking-tight">FA reference rules · Team name mapping</div>
            <p className="text-[11px] opacity-60 mt-0.5">Adjust age-group durations/pitch sizes and map FA team names to your squads. These drive every import below.</p>
          </div>
        </button>
        {showRules && (
          <div className="p-5 border-t border-border">
            <AdminFARulesEditor onSaved={() => { if (result) preview(); }} />
          </div>
        )}
      </div>

      <div className="card-tactical rounded-md p-5 space-y-4">
        <div>
          <div className="font-display font-bold text-2xl tracking-tight">Import from FA Full-Time</div>
          <p className="text-sm opacity-60 mt-1">Paste your club's FA Full-Time fixtures URL. We'll pull every home fixture, match teams to your squads, and create bookings on your pitches.</p>
        </div>
        <div className="grid md:grid-cols-12 gap-3">
          <div className="md:col-span-8">
            <Label className="text-xs uppercase tracking-widest opacity-70">FA Full-Time URL</Label>
            <Input
              data-testid="fa-url-input"
              className="mt-2 font-mono text-xs"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://fulltime.thefa.com/fixtures/1/100.html?selectedSeason=..."
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest opacity-70">Default kick-off</Label>
            <Input
              data-testid="fa-default-kickoff"
              className="mt-2"
              value={defaultKickoff}
              onChange={e => setDefaultKickoff(e.target.value)}
              placeholder="10:00"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest opacity-70">Club prefix</Label>
            <Input
              data-testid="fa-club-prefix"
              className="mt-2"
              value={clubPrefix}
              onChange={e => setClubPrefix(e.target.value)}
              placeholder="Elsenham"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={preview}
            disabled={loading}
            data-testid="fa-preview-btn"
            className="text-[10px] tracking-widest uppercase px-4 py-2 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
            {loading ? "Fetching…" : "Preview home fixtures"}
          </button>
        </div>
      </div>

      {result && unmatchedNames.length > 0 && (
        <div className="card-tactical rounded-md p-5 space-y-3" data-testid="fa-unmatched-panel">
          <div>
            <div className="font-display font-bold text-xl tracking-tight text-amber-300">{unmatchedNames.length} unmatched FA name{unmatchedNames.length === 1 ? "" : "s"}</div>
            <p className="text-xs opacity-60 mt-1">These FA Full-Time home team names don&apos;t match any of your squads. Pick a squad and save the mapping so next time it auto-matches.</p>
          </div>
          <div className="divide-y divide-border border border-border rounded overflow-hidden">
            {unmatchedNames.map(name => (
              <div key={name} className="p-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={`fa-unmatched-${name.replace(/\s+/g,"-").toLowerCase()}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm truncate">{name}</div>
                </div>
                <div className="md:w-72">
                  <Select
                    value={unmatchedResolving[name] || ""}
                    onValueChange={v => setUnmatchedResolving(prev => ({ ...prev, [name]: v }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue placeholder="Pick a squad…" /></SelectTrigger>
                    <SelectContent>
                      {(result?.teams || []).map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.age_group ? ` · ${t.age_group}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <button
                  type="button"
                  onClick={() => saveOneMapping(name)}
                  disabled={!unmatchedResolving[name]}
                  data-testid={`fa-unmatched-save-${name.replace(/\s+/g,"-").toLowerCase()}`}
                  className="text-[10px] tracking-widest uppercase px-3 py-2 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5 disabled:opacity-40"
                >
                  <Link2 className="w-3 h-3" /> Save mapping
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {result && (result.club_squads?.length || 0) > 0 && (
        <div className="card-tactical rounded-md p-5 space-y-3" data-testid="fa-squads-panel">
          <div className="flex items-baseline justify-between flex-wrap gap-3">
            <div>
              <div className="font-display font-bold text-xl tracking-tight">Club squads on this page</div>
              <p className="text-xs opacity-60 mt-1">
                {missingSquads.length === 0
                  ? `All ${result.club_squads.length} squad(s) already exist locally — fixtures will auto-match.`
                  : `${missingSquads.length} of ${result.club_squads.length} squad(s) haven't been created yet. Create them so fixtures auto-map to a team.`}
              </p>
            </div>
            {missingSquads.length > 0 && (
              <button
                onClick={createMissingSquads}
                disabled={squadsCreating}
                data-testid="fa-create-squads-btn"
                className="text-[10px] tracking-widest uppercase px-4 py-2 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                {squadsCreating ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                {squadsCreating ? "Creating…" : `Create ${missingSquads.length} missing squad${missingSquads.length === 1 ? "" : "s"}`}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {result.club_squads.map(s => (
              <span
                key={s.name}
                data-testid={`fa-squad-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
                className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded border ${
                  s.already_exists
                    ? "border-[#83B68B]/40 bg-[#83B68B]/10 text-[#83B68B]"
                    : "border-amber-500/40 bg-amber-500/10 text-amber-400"
                }`}
              >
                {s.name}
                <span className="opacity-60 ml-1.5 normal-case tracking-normal">
                  {s.age_group}{s.game_format ? ` · ${s.game_format}` : ""}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {result && (
        <div className="card-tactical rounded-md overflow-hidden">
          <div className="p-5 flex items-baseline justify-between flex-wrap gap-3 border-b border-border">
            <div>
              <div className="font-display font-bold text-xl tracking-tight">{result.count_home} home fixture{result.count_home === 1 ? "" : "s"} found</div>
              <p className="text-xs opacity-60 mt-1">Only rows where the home team starts with "{result.club_prefix}" are shown. Away fixtures are ignored.</p>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-[10px] tracking-widest uppercase opacity-70 inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  data-testid="fa-select-all"
                  checked={allSelectable.length > 0 && allSelectable.every(r => r.selected)}
                  onChange={e => toggleAll(e.target.checked)}
                />
                Select all
              </label>
              <button
                onClick={importSelected}
                disabled={importing || selectedCount === 0}
                data-testid="fa-import-btn"
                className="text-[10px] tracking-widest uppercase px-4 py-2 rounded inline-flex items-center gap-2 transition-colors disabled:opacity-50 bg-[#83B68B] text-black hover:bg-[#83B68B]"
              >
                {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                {importing ? "Sending…" : `Send ${selectedCount} to staging`}
              </button>
            </div>
          </div>
          {rows.length === 0 ? (
            <div className="p-8 text-sm opacity-60 text-center">No home fixtures on this page. Check the URL or the club prefix.</div>
          ) : (
            <>
              <div className="p-4 bg-muted/30 border-b border-border flex flex-wrap items-end gap-3" data-testid="fa-bulk-bar">
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Bulk pitch</Label>
                  <div className="mt-1 inline-flex items-center gap-2">
                    <Select value={bulkPitchId} onValueChange={setBulkPitchId}>
                      <SelectTrigger data-testid="fa-bulk-pitch-select" className="h-8 w-52 text-xs"><SelectValue placeholder="Pick pitch to apply" /></SelectTrigger>
                      <SelectContent>
                        {(result.pitches || []).map(p => {
                          const loc = p.location || p.venue_name || "";
                          const paren = loc.match(/\(([^)]+)\)/);
                          const auto = paren ? paren[1].toUpperCase().slice(0, 4) : loc.split(/\s+/).map(w => w[0] || "").join("").toUpperCase().slice(0, 3);
                          const acronym = p.venue_short_code || auto;
                          return <SelectItem key={p.id} value={p.id}>{acronym ? `[${acronym}] ` : ""}{p.name}{p.venue_name ? ` · ${p.venue_name}` : ""}{p.game_format ? ` · ${p.game_format}` : ""}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                    <button
                      onClick={applyBulkPitch}
                      disabled={!bulkPitchId || selectedCount === 0}
                      data-testid="fa-apply-bulk-pitch"
                      className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-border hover:border-[#83B68B] hover:text-[#83B68B] rounded inline-flex items-center gap-1.5 transition-colors disabled:opacity-50"
                    >
                      <MapPinned className="w-3 h-3" /> Apply to selected
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Turnaround (min)</Label>
                  <Input
                    data-testid="fa-turnaround"
                    className="mt-1 h-8 w-24 text-xs"
                    type="number"
                    min={0}
                    max={60}
                    step={15}
                    value={turnaroundMin}
                    onChange={e => setTurnaroundMin(Math.min(60, Math.max(0, parseInt(e.target.value) || 0)))}
                  />
                </div>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Latest kick-off</Label>
                  <Input
                    data-testid="fa-latest-kickoff"
                    className="mt-1 h-8 w-24 text-xs"
                    value={latestKickoff}
                    onChange={e => setLatestKickoff(e.target.value)}
                    placeholder="15:00"
                  />
                </div>
                <div className="ml-auto">
                  <button
                    onClick={autoSchedule}
                    disabled={selectedCount === 0}
                    data-testid="fa-auto-schedule"
                    className="text-[10px] tracking-widest uppercase px-3 py-1.5 border border-[#83B68B]/40 text-[#83B68B] hover:bg-[#83B68B]/10 rounded inline-flex items-center gap-1.5 transition-colors disabled:opacity-50"
                  >
                    <Wand2 className="w-3 h-3" /> Auto-schedule selected
                  </button>
                  <div className="text-[10px] opacity-70 mt-1 text-right max-w-[18rem]">Stacks each pitch from "{defaultKickoff}", youngest first, {turnaroundMin} min turnaround. Existing bookings on the same date + pitch are skipped, and games that would kick off after {normalizedLatest} spill onto a spare pitch.</div>
                </div>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="fa-fixtures-table">
                <thead className="bg-muted text-[10px] tracking-widest uppercase opacity-60">
                  <tr>
                    <th className="text-left px-3 py-3 w-8"></th>
                    <th className="text-left px-3 py-3">Date</th>
                    <th className="text-left px-3 py-3">Kick-off</th>
                    <th className="text-left px-3 py-3">Home</th>
                    <th className="text-left px-3 py-3">Opponent</th>
                    <th className="text-left px-3 py-3">Pitch</th>
                    <th className="text-left px-3 py-3">Comp</th>
                    <th className="text-left px-3 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const rowLocked = r.already_imported;
                    return (
                      <tr key={r.fa_id} data-testid={`fa-row-${r.fa_id}`} className={`border-t border-border ${rowLocked ? "opacity-50" : ""}`}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            data-testid={`fa-select-${r.fa_id}`}
                            disabled={rowLocked}
                            checked={!!r.selected && !rowLocked}
                            onChange={e => update(i, {selected: e.target.checked})}
                          />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{format(parseISO(r.date), "EEE d MMM yy")}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            <select
                              disabled={rowLocked}
                              data-testid={`fa-start-hour-${r.fa_id}`}
                              value={r.suggested_start_hour}
                              onChange={e => {
                                const sh = parseInt(e.target.value);
                                const dur = r.duration_min || 60;
                                const endTotal = sh * 60 + r.suggested_start_min + dur;
                                const eh = Math.floor(endTotal / 60);
                                const em = Math.min(45, Math.max(0, endTotal % 60));
                                const emSnap = em % 15 === 0 ? em : (Math.floor(em / 15) + 1) * 15;
                                update(i, {
                                  suggested_start_hour: sh,
                                  suggested_end_hour: emSnap === 60 ? eh + 1 : eh,
                                  suggested_end_min: emSnap === 60 ? 0 : emSnap,
                                });
                              }}
                              className="bg-transparent border border-border rounded px-1 py-0.5 text-xs"
                            >
                              {HH.map(h => <option key={h} value={h}>{String(h).padStart(2,"0")}</option>)}
                            </select>
                            <span>:</span>
                            <select
                              disabled={rowLocked}
                              data-testid={`fa-start-min-${r.fa_id}`}
                              value={r.suggested_start_min}
                              onChange={e => {
                                const sm = parseInt(e.target.value);
                                const dur = r.duration_min || 60;
                                const endTotal = r.suggested_start_hour * 60 + sm + dur;
                                const eh = Math.floor(endTotal / 60);
                                const em = endTotal % 60;
                                const emSnap = em % 15 === 0 ? em : (Math.floor(em / 15) + 1) * 15;
                                update(i, {
                                  suggested_start_min: sm,
                                  suggested_end_hour: emSnap === 60 ? eh + 1 : eh,
                                  suggested_end_min: emSnap === 60 ? 0 : emSnap,
                                });
                              }}
                              className="bg-transparent border border-border rounded px-1 py-0.5 text-xs"
                            >
                              {MM.map(m => <option key={m} value={m}>{String(m).padStart(2,"0")}</option>)}
                            </select>
                            <span
                              data-testid={`fa-end-time-${r.fa_id}`}
                              className={`text-[10px] ml-1 ${r.suggested_end_hour >= 21 ? "text-amber-400" : "opacity-60"}`}
                            >
                              → {String(r.suggested_end_hour).padStart(2,"0")}:{String(r.suggested_end_min).padStart(2,"0")}
                              <span className="opacity-70 ml-1">({r.duration_min}m)</span>
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="whitespace-nowrap">{r.home_team}</div>
                          <div className="text-[10px] opacity-60">{r.matched_team_id ? `→ ${r.matched_team_name} · ${r.matched_age_group || ""}` : <span className="text-amber-400">No matching team</span>}</div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.away_team}</td>
                        <td className="px-3 py-2">
                          <Select
                            value={r.suggested_pitch_id || ""}
                            onValueChange={v => update(i, {suggested_pitch_id: v, suggested_pitch_name: (result.pitches.find(p => p.id === v) || {}).name, spillover_moved_from: null, spillover_late: false})}
                            disabled={rowLocked}
                          >
                            <SelectTrigger className="h-8 text-xs w-40"><SelectValue placeholder="Pick pitch" /></SelectTrigger>
                            <SelectContent>
                          {result.pitches.map(p => {
                            const loc = p.location || p.venue_name || "";
                            const paren = loc.match(/\(([^)]+)\)/);
                            const auto = paren ? paren[1].toUpperCase().slice(0, 4) : loc.split(/\s+/).map(w => w[0] || "").join("").toUpperCase().slice(0, 3);
                            const acronym = p.venue_short_code || auto;
                            return <SelectItem key={p.id} value={p.id}>{acronym ? `[${acronym}] ` : ""}{p.name}{p.venue_name ? ` · ${p.venue_name}` : ""}{p.game_format ? ` · ${p.game_format}` : ""}</SelectItem>;
                          })}
                            </SelectContent>
                          </Select>
                          {r.spillover_moved_from && (
                            <div
                              data-testid={`fa-spill-${r.fa_id}`}
                              className="text-[10px] tracking-widest uppercase mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 border border-blue-500/30"
                            >
                              <MapPinned className="w-2.5 h-2.5" /> spilled from {r.spillover_moved_from}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs opacity-80">{r.competition}</td>
                        <td className="px-3 py-2">
                          {r.already_imported ? (
                            <span className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-muted opacity-80">imported</span>
                          ) : r.skip_reason ? (
                            <span className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-red-500/20 text-red-500">{r.skip_reason}</span>
                          ) : r.spillover_late ? (
                            <span data-testid={`fa-late-${r.fa_id}`} className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">no free slot</span>
                          ) : (
                            <span className="text-[10px] opacity-40">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}

      <FAStagingPanel reloadKey={stagingReloadKey} />
    </div>
  );
}
